import { useState, useEffect, useCallback, useMemo } from 'react';
// Import the valuation formula and types
import { calculateOverallPlayerScore, filterPlayersWithSummary } from '../utils/playerValue';
import type { LeagueSettings, TeamOffensiveStats, LeagueAverages, PlayerStats, InjuryHistory } from '../utils/playerValue';
import type { Player as BasePlayer } from '../types/player';
import { getProcessedSleeperData } from '../utils/sleeperIntegration';
import { useProjections } from '../hooks/useProjections';
import { PageLoader } from '../components/LoadingSpinner';
import { getPlayerImageWithOverrides, debugPlayerImageMapping } from '../utils/playerImageFix';

// Extend base Player with UI-specific fields and formula-required fields
type Player = BasePlayer & { 
  rank: number; 
  change: number; 
  projection: number; 
  age: number; 
  lastYearPoints: number; 
  teamStats?: TeamOffensiveStats; 
  leagueAverages?: LeagueAverages; 
  playerStats?: PlayerStats; 
  injuryHistory?: InjuryHistory[];
};

type PositionalScarcity = {
  position: string;
  replacementThreshold: number;
};

// Define fantasy-relevant positions and their replacement thresholds
const POSITIONAL_SCARCITY: Record<string, PositionalScarcity> = {
  QB: { position: 'QB', replacementThreshold: 12 },
  RB: { position: 'RB', replacementThreshold: 24 },
  WR: { position: 'WR', replacementThreshold: 24 },
  TE: { position: 'TE', replacementThreshold: 12 },
  K: { position: 'K', replacementThreshold: 12 },
  DEF: { position: 'DEF', replacementThreshold: 12 },
};

// Fantasy-relevant positions for filtering
const FANTASY_POSITIONS = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];

export const PlayerRankings = () => {
  const { 
    projections: generatedProjections, 
    loading: projectionsLoading, 
    error: projectionsError 
  } = useProjections();
  const [searchQuery, setSearchQuery] = useState('');
  const [positionFilter, setPositionFilter] = useState('All');
  const [sortField, setSortField] = useState<keyof Player | 'rank' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [players, setPlayers] = useState<Player[]>([]);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [pageError, setPageError] = useState<string | null>(null);

  // Define league settings for player valuation
  const leagueSettings: LeagueSettings = useMemo(() => ({
    leagueSize: 12, 
    startingSlots: { QB:1, RB:2, WR:2, TE:1, K:1, DEF:1 } 
  }), []);
  
  const fetchPlayers = useCallback(async () => {
    setPageLoading(true); // Indicate that player processing has started
    setPageError(null);   // Clear previous errors

    // Critical check: ensure projections are actually available and not empty
    // This is a safeguard, as useEffect should also handle this.
    if (!generatedProjections || Object.keys(generatedProjections).length === 0) {
      setPageError("Projections data is not available or is empty. Cannot rank players.");
      setPlayers([]);
      setPageLoading(false); // Explicitly set loading false here before returning
      return; 
    }
    try {
      // Fetch Sleeper players (basic data)
      const sleeperPlayersRes = await fetch('https://api.sleeper.app/v1/players/nfl');
      const sleeperPlayersData = await sleeperPlayersRes.json();

      const offensePlayers = Object.values(sleeperPlayersData).filter((p: any) =>
        p.position !== 'DEF' && p.active && p.position && p.team && p.player_id && p.full_name
      );
      const defRes = await fetch('https://api.sleeper.app/v1/players/nfl?position=DEF');
      const defData = await defRes.json();
      const defensePlayers = Object.values(defData);
      const allSleeperPlayers = [...offensePlayers, ...defensePlayers];

      // Fetch ESPN athletes for images/age
      const espnRes = await fetch('https://sports.core.api.espn.com/v3/sports/football/nfl/athletes?limit=18000');
      const espnData = await espnRes.json();
      const espnAthletes = espnData.items || [];
      const espnMap = new Map();
      for (const athlete of espnAthletes) {
        if (athlete.fullName) {
          espnMap.set(athlete.fullName.toLowerCase(), athlete);
        }
      }

      // Fetch team logos for defenses
      const teamsRes2 = await fetch('https://site.api.espn.com/apis/site/v2/sports/football/nfl/teams');
      const teamsJson2 = await teamsRes2.json();
      const teamLogoMap = new Map<string, string>();
      (teamsJson2.sports?.[0]?.leagues?.[0]?.teams || []).forEach((entry: any) => {
        const t = entry.team;
        if (t.abbreviation && t.logos?.length) teamLogoMap.set(t.abbreviation, t.logos[0].href);
      });

      // Fetch processed stats (excluding projections from here)
      const { statsMap, playerStatsMap, teamStatsMap, leagueAverages } = await getProcessedSleeperData(2024); // Using 2024 (last season)
      
      // Ensure league averages are always available with realistic defaults
      const defaultLeagueAverages: LeagueAverages = {
        playsPerGame: 65,
        pointsPerGame: 22.5,
        redZoneScoreRate: 0.57
      };
      const finalLeagueAverages = leagueAverages || defaultLeagueAverages;

      if (!generatedProjections) {
        console.error('PlayerRankings.tsx: generatedProjections from useProjections are not available. Cannot proceed with player ranking.');
        setPageError('Projection data is missing.');
        setPageLoading(false);
        return;
      }
      console.log(`PlayerRankings.tsx: Using ${Object.keys(generatedProjections).length} projections from useProjections hook.`);

      // Filter for valid fantasy players using centralized function (exclude team defenses)
      const validPlayers = filterPlayersWithSummary(allSleeperPlayers, 'PlayerRankings', false);
      const fantasyPlayers = validPlayers.filter((p: any) => {
        // Additional filtering for recent activity/relevance
        // Filter out players who haven't played in last 2 seasons (2022, 2023)
        // If they have years_exp of 0 or are rookies, include them
        if (p.years_exp === 0 || p.years_exp === null || p.years_exp === undefined) return true;
        
        // Check if they have recent season stats or are young enough to be relevant
        const hasRecentStats = statsMap.has(String(p.player_id));
        const isYoungPlayer = p.age && p.age < 27;
        const isExperiencedPlayer = p.years_exp && p.years_exp > 1;
        
        return hasRecentStats || isYoungPlayer || isExperiencedPlayer;
      });

      console.log(`Filtered to ${fantasyPlayers.length} active fantasy players from ${allSleeperPlayers.length} total players`);

      const playerArr: Player[] = fantasyPlayers.map((basePlayer: any): Player => {
        const name = basePlayer.full_name ?? (basePlayer.position === 'DEF' ? `${basePlayer.team} DEF` : `${basePlayer.first_name} ${basePlayer.last_name}`);
        const espnPlayer = espnMap.get(basePlayer.full_name?.toLowerCase());
        let determinedAge: number | null = null;

        if (espnPlayer && typeof espnPlayer.age === 'number') {
          determinedAge = espnPlayer.age;
        } else if (basePlayer.age !== undefined && basePlayer.age !== null && typeof basePlayer.age === 'number') {
          determinedAge = basePlayer.age; // Fallback to Sleeper's age
        }

        // For DEF players, if no age is found, default to 0.
        // For other positions, if no age is found, default to a general age like 25 or handle as null if preferred downstream.
        const ageToUse = basePlayer.position === 'DEF' ? (determinedAge ?? 0) : (determinedAge ?? 25);

        const imageUrl = basePlayer.position === 'DEF' 
          ? teamLogoMap.get(basePlayer.team) || '/images/defense-placeholder.png' 
          : getPlayerImageWithOverrides(
              basePlayer.full_name,
              espnPlayer?.id?.toString(),
              espnPlayer?.headshot?.href || '/images/player-placeholder.png'
            );

        // Debug image mapping for specific players
        if (['Lamar Jackson', 'Josh Allen', 'Patrick Mahomes'].includes(name)) {
          debugPlayerImageMapping(name, espnPlayer?.id?.toString());
        }

        const projection = generatedProjections[String(basePlayer.player_id)] ?? 0;
        
        // Get season stats for last year points calculation
        const seasonStatsData = statsMap.get(String(basePlayer.player_id));
        const totalPoints = seasonStatsData?.fpts_ppr ?? seasonStatsData?.fpts_half_ppr ?? seasonStatsData?.fpts_std ?? 0;
        
        // Debug logging for a few players to see what data we're getting
        if (['Josh Allen', 'Christian McCaffrey', 'Cooper Kupp'].includes(name)) {
          console.log(`DEBUG ${name} stats:`, {
            player_id: basePlayer.player_id,
            seasonStatsData,
            totalPoints,
            statsMapSize: statsMap.size
          });
        }
        
        // Simple last year total points calculation using actual season data
        let lastYearPoints = 0;
        if (totalPoints > 0) {
          // Use actual total season points when available
          lastYearPoints = totalPoints;
        } else {
          // For players without 2024 data, show 0 (rookies, injured players, etc.)
          lastYearPoints = 0;
        }

        // Enhanced player stats with realistic estimates for ALL players
        const currentPStats: PlayerStats = playerStatsMap.get(String(basePlayer.player_id)) || (() => {
          // Base stats by position and estimated role
          const yearExp = basePlayer.years_exp || 0;
          const roleMultiplier = yearExp > 3 ? 1.3 : yearExp > 1 ? 1.1 : 0.8; // Experience factor
          
          switch (basePlayer.position) {
            case 'WR':
              return {
                targets: Math.round((60 + (projection / 4)) * roleMultiplier),
                carries: 2,
                redZoneOpportunities: Math.round((8 + (projection / 20)) * roleMultiplier),
                airYards: Math.round((800 + (projection * 3)) * roleMultiplier),
                schedule: []
              };
            case 'RB':
              return {
                targets: Math.round((30 + (projection / 8)) * roleMultiplier),
                carries: Math.round((120 + (projection / 2)) * roleMultiplier),
                redZoneOpportunities: Math.round((15 + (projection / 15)) * roleMultiplier),
                airYards: Math.round((200 + (projection / 2)) * roleMultiplier),
                schedule: []
              };
            case 'TE':
              return {
                targets: Math.round((50 + (projection / 5)) * roleMultiplier),
                carries: 1,
                redZoneOpportunities: Math.round((12 + (projection / 12)) * roleMultiplier),
                airYards: Math.round((500 + (projection * 2)) * roleMultiplier),
                schedule: []
              };
            case 'QB':
              return {
                targets: 0,
                carries: Math.round((40 + (projection / 8)) * roleMultiplier),
                redZoneOpportunities: Math.round((25 + (projection / 10)) * roleMultiplier),
                airYards: 0,
                schedule: []
              };
            default:
              return {
                targets: 0,
                carries: 0,
                redZoneOpportunities: 0,
                airYards: 0,
                schedule: []
              };
          }
        })();

        return {
          ...basePlayer, // Spread all properties from Sleeper's basePlayer object
          id: String(basePlayer.player_id), // Ensure id is a string and consistent
          name,
          // position, team, first_name, last_name, status, player_id etc. are now covered by ...basePlayer
          // Override or add specific fields for the Player type:
          rank: 0, // Initial rank, will be updated
          value: 0, // To be calculated by valuation formula
          change: 0, // Placeholder for rank change
          imageUrl,
          projection,
          age: ageToUse, // Use the determined age with fallback
          lastYearPoints,
          teamStats: teamStatsMap.get(basePlayer.team) || (leagueSettings.startingSlots[basePlayer.position] ? (() => {
            // Enhanced team stats based on actual team quality
            const teamQualityMap: Record<string, { pace: number; scoring: number; efficiency: number; oline: number }> = {
              KC: { pace: 1.15, scoring: 1.2, efficiency: 1.15, oline: 8 },
              BUF: { pace: 1.12, scoring: 1.15, efficiency: 1.12, oline: 10 },
              CIN: { pace: 1.1, scoring: 1.1, efficiency: 1.1, oline: 12 },
              LAC: { pace: 1.08, scoring: 1.05, efficiency: 1.08, oline: 14 },
              PHI: { pace: 1.1, scoring: 1.12, efficiency: 1.1, oline: 9 },
              SF: { pace: 1.12, scoring: 1.1, efficiency: 1.15, oline: 7 },
              DAL: { pace: 1.08, scoring: 1.08, efficiency: 1.05, oline: 11 },
              MIA: { pace: 1.08, scoring: 1.05, efficiency: 1.08, oline: 15 }
            };
            
            const teamMods = teamQualityMap[basePlayer.team] || { pace: 1.0, scoring: 1.0, efficiency: 1.0, oline: 16 };
            
            return {
              playsPerGame: Math.round(65 * teamMods.pace),
              pointsPerGame: Math.round(22 * teamMods.scoring),
              redZoneScoreRate: Math.min(0.8, 0.55 * teamMods.efficiency),
              offensiveLineRank: teamMods.oline,
              totalTargets: Math.round(500 * teamMods.pace),
              totalCarries: Math.round(400 * teamMods.pace),
              totalRedZoneOpportunities: Math.round(80 * teamMods.efficiency),
              totalAirYards: Math.round(3500 * teamMods.pace),
              winPercentage: Math.min(0.9, 0.5 * teamMods.scoring),
              avgTimeOfPossession: Math.round(30 * (teamMods.pace > 1.05 ? 0.95 : 1.05))
            };
          })() : undefined),
                      leagueAverages: finalLeagueAverages,
          playerStats: currentPStats,
          // injuryHistory is likely already on basePlayer, but ensure it's an array
          injuryHistory: basePlayer.injury_history || [], 
        };
      });

      // Debug: Log data for a few sample players
      const debugNames = [
        'Christian McCaffrey',
        'Justice Hill',
        'Benny LeMay'
      ];
      playerArr.forEach(player => {
        if (debugNames.includes(player.name)) {
          // Log all relevant fields for ranking
          console.log('DEBUG Player Data:', {
            name: player.name,
            id: player.id,
            position: player.position,
            team: player.team,
            age: player.age,
            projection: player.projection,
            lastYearPoints: player.lastYearPoints,
            teamStats: player.teamStats,
            playerStats: player.playerStats,
            injuryHistory: player.injuryHistory
          });
        }
      });

      const tempPlayersForReplacementCalc = playerArr.map(pl => ({ ...pl, projection: pl.projection || 0 }));
      const replacementLevels: Record<string, number> = {};
      FANTASY_POSITIONS.forEach(pos => {
        if (pos === 'DEF') return; // Skip DEF for this replacement level style if needed or handle differently
        const posPlayers = tempPlayersForReplacementCalc.filter(p => p.position === pos).sort((a, b) => b.projection - a.projection);
        const threshold = POSITIONAL_SCARCITY[pos]?.replacementThreshold || 12;
        replacementLevels[pos] = posPlayers[threshold - 1]?.projection || 0;
      });
      
      const playersWithScore = playerArr.map((player) => {
        if (!FANTASY_POSITIONS.includes(player.position)) return { ...player, value: 0 };
        const replacementLevel = replacementLevels[player.position] || 0;
        try {
          const { overallScore } = calculateOverallPlayerScore(player, leagueSettings, playerArr, replacementLevel);
          // Check for NaN or Infinity. If so, default to 0. Otherwise, use the calculated score.
          const finalScore = (!isNaN(overallScore) && isFinite(overallScore)) ? overallScore : 0;
          return { ...player, value: finalScore };
        } catch (e) {
          console.error(`Error calculating score for ${player.name}:`, e);
          return { ...player, value: 0 }; // Fallback to 0 on error
        }
      });

      const sortedPlayers = [...playersWithScore].sort((a, b) => b.value - a.value);
      const rankedPlayers = sortedPlayers.map((player, index) => ({
        ...player,
        rank: index + 1,
      }));

      setPlayers(rankedPlayers);
      setPageError(null); // Clear any previous errors on success
    } catch (err: any) {
      console.error('Failed to fetch or process players:', err);
      setPageError(err.message || 'An unknown error occurred while fetching player data');
    } finally {
      setPageLoading(false);
    }
  }, [generatedProjections, leagueSettings, setPageError, setPageLoading, setPlayers]);

  useEffect(() => {
    if (projectionsLoading) {
      setPageLoading(true);
      setPageError(null);
      return;
    }

    if (projectionsError) {
      setPageError(`Failed to load projections: ${projectionsError}`);
      setPlayers([]);
      setPageLoading(false);
      return;
    }

    // At this point, projections are not loading and there's no error.
    if (generatedProjections && Object.keys(generatedProjections).length > 0) {
      fetchPlayers();
    } else {
      // Projections are loaded (no error, not loading), but no data or empty data.
      setPageError("No projection data available to rank players.");
      setPlayers([]);
      setPageLoading(false);
    }
  }, [projectionsLoading, projectionsError, generatedProjections, fetchPlayers]);

  const positions = ['All', 'QB', 'RB', 'WR', 'TE', 'K'];

  const handleSort = (field: keyof Player | 'rank') => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        // Third click: reset to default (no sorting)
        setSortField(null);
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedPlayers = players
    .filter(player => {
      const nameMatch = player.name.toLowerCase().includes(searchQuery.toLowerCase());
      const positionMatch = positionFilter === 'All' || player.position === positionFilter;
      return nameMatch && positionMatch;
    })
    .sort((a, b) => {
      // If no sort field selected, maintain original ranking order
      if (!sortField) {
        return a.rank - b.rank;
      }
      
      let valueA: any, valueB: any;
      
      switch (sortField) {
        case 'rank':
          valueA = a.rank;
          valueB = b.rank;
          break;
        case 'name':
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
          break;
        case 'position':
          valueA = a.position;
          valueB = b.position;
          break;
        case 'team':
          valueA = a.team;
          valueB = b.team;
          break;
        case 'age':
          valueA = a.age;
          valueB = b.age;
          break;
        case 'projection':
          valueA = a.projection;
          valueB = b.projection;
          break;
        case 'value':
          valueA = a.value;
          valueB = b.value;
          break;
        case 'lastYearPoints':
          valueA = a.lastYearPoints;
          valueB = b.lastYearPoints;
          break;
        default:
          return 0;
      }

      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  if (pageLoading) return (
    <PageLoader 
      pageName="Player Rankings"
      subtitle="Comprehensive player rankings updated with advanced metrics and projections"
      loadingText="Loading player rankings..."
    />
  );
  if (pageError) return <div className="text-center py-10 text-red-500">Error: {pageError}</div>;

  return (
    <div className="container mx-auto p-4 bg-neutral-900 min-h-screen text-neutral-100">
      <h1 className="text-4xl font-bold text-center mb-8 text-yellow-400">Player Rankings</h1>
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-center">
        <input
          type="text"
          placeholder="Search players..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="p-3 rounded-lg bg-neutral-800 text-white border border-neutral-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent flex-grow"
        />
        <select
          value={positionFilter}
          onChange={(e) => setPositionFilter(e.target.value)}
          className="p-3 rounded-lg bg-neutral-800 text-white border border-neutral-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none min-w-[100px]"
        >
          {positions.map(pos => <option key={pos} value={pos}>{pos}</option>)}
        </select>
      </div>
      {filteredAndSortedPlayers.length === 0 && !pageLoading ? (
        <div className="text-center py-10 text-neutral-400">No players match your criteria.</div>
      ) : (
        <>
          {/* Mobile Card Layout */}
          <div className="md:hidden space-y-3">
            {filteredAndSortedPlayers.map((player: Player) => (
              <div key={player.id} className="bg-neutral-800 rounded-lg p-4 border border-neutral-700 hover:border-purple-500 transition-colors duration-150">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-neutral-700 rounded-full flex items-center justify-center text-xs font-bold text-yellow-400">
                        #{player.rank}
                      </div>
                    </div>
                    <img 
                      src={player.imageUrl} 
                      alt={player.name} 
                      className="w-12 h-12 rounded-full object-cover bg-neutral-700 flex-shrink-0" 
                      onError={(e) => { (e.target as HTMLImageElement).src = '/images/player-placeholder.png'; }} 
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white text-base truncate">{player.name}</h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          player.position === 'QB' ? 'bg-red-900 text-red-200' :
                          player.position === 'RB' ? 'bg-green-900 text-green-200' :
                          player.position === 'WR' ? 'bg-blue-900 text-blue-200' :
                          player.position === 'TE' ? 'bg-yellow-900 text-yellow-200' :
                          'bg-gray-900 text-gray-200'
                        }`}>
                          {player.position}
                        </span>
                        <span className="text-sm text-neutral-300">{player.team}</span>
                        <span className="text-sm text-neutral-400">Age {player.age}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-neutral-700">
                  <div className="text-center">
                    <div className="text-xs text-neutral-400 uppercase tracking-wide">Projection</div>
                    <div className="text-sm font-semibold text-neutral-100 mt-1">{player.projection?.toFixed(1)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-neutral-400 uppercase tracking-wide">Value</div>
                    <div className="text-sm font-semibold text-purple-400 mt-1">{player.value?.toFixed(2)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-neutral-400 uppercase tracking-wide">Last Year</div>
                    <div className="text-sm font-semibold text-neutral-100 mt-1">
                      {player.lastYearPoints && player.lastYearPoints > 0 ? player.lastYearPoints.toFixed(1) : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table Layout */}
          <div className="hidden md:block overflow-x-auto rounded-lg shadow-lg bg-neutral-950">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-neutral-800">
                <tr>
                  {[
                    { key: 'rank', label: 'Rank' },
                    { key: 'name', label: 'Player' },
                    { key: 'position', label: 'Pos' },
                    { key: 'team', label: 'Team' },
                    { key: 'age', label: 'Age' },
                    { key: 'projection', label: 'Projection' },
                    { key: 'value', label: 'Value' },
                    { key: 'lastYearPoints', label: 'Last Year' }
                  ].map(({ key, label }) => (
                    <th 
                      key={key} 
                      scope="col" 
                      className="px-4 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider cursor-pointer hover:bg-neutral-700 transition-colors"
                      onClick={() => handleSort(key as keyof Player | 'rank')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{label}</span>
                        <span className="text-neutral-500">
                          {sortField === key ? (
                            sortDirection === 'asc' ? '↑' : '↓'
                          ) : (
                            '↕'
                          )}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-neutral-900 divide-y divide-gray-700">
                {filteredAndSortedPlayers.map((player: Player) => (
                  <tr key={player.id} className="hover:bg-neutral-800 transition-colors duration-150">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-neutral-100">{player.rank}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-100">
                      <div className="flex items-center">
                        <img 
                          src={player.imageUrl} 
                          alt={player.name} 
                          className="w-10 h-10 rounded-full mr-3 object-cover bg-neutral-700" 
                          onError={(e) => { (e.target as HTMLImageElement).src = '/images/player-placeholder.png'; }} 
                        />
                        {player.name}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-300">{player.position}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-300">{player.team}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-300">{player.age}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-300">{player.projection?.toFixed(1)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-purple-400">{player.value?.toFixed(2)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-300">
                      {player.lastYearPoints && player.lastYearPoints > 0 ? player.lastYearPoints.toFixed(1) : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};